import React, { useMemo } from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    OnChangeFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
    Row
} from "@tanstack/react-table";
import { Box, Spinner, Text, useColorMode } from "theme-ui";
import { TabelClassname } from "../../../../../utils/Classname";
import TablePagination from "../../../../Tabels/Pagination";
import theme from "../../../../../theme";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

interface DataTableProps<T extends object> {
    data: T[];
    columns: ColumnDef<T>[];
    className?: string | undefined;
    onPageChange: (page: number | any) => void,
    onPerPageChange: (value: string) => void,
    currentPage?: any,
    TotalCount?: number;
    TotalLength?: any;
    perPageCount: number,
    showNavigation?: boolean;
    enableColumnFilters?: boolean,
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState> | any,
    loader?: boolean;
    fixedColumn?: boolean;
    withoutURLFilter?: boolean;
    onDragEnd?: (event: DragEndEvent) => void;
    dndKey?: string | number;
}

export const RowDragHandleCell = ({ row }: { row: any }) => {
    const [colorMode] = useColorMode();
    const { attributes, listeners } = useSortable({
        id: row.original._id,
    })

    return (
        <button {...attributes} {...listeners} style={{ border: "none", backgroundColor: colorMode === "light" ? "#ffffff" : "#282e42", color: colorMode === "light" ? "#000000" : "#ffffff", cursor: "grab" }}>
            🟰
        </button>
    )
}

const DraggableRow = ({ row, fixedColumn }: { row: Row<any>; fixedColumn?: boolean }): JSX.Element => {
    const {
        transform,
        transition,
        setNodeRef,
        isDragging
    } = useSortable({
        id: row.original._id,
    });

    const style = React.useMemo(() => ({
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1 : 0,
        position: 'relative' as const,
    }), [transform, transition, isDragging]);

    const [colorMode] = useColorMode();
    const commonPadding = localStorage.getItem("tPadding") || '13px';

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`table-hover-row ${colorMode === "light" ? "light-mode" : "dark-mode"}`}
        >
            {row.getVisibleCells().map((cell, cellIndex) => {
                return (
                    <td
                        className={`fixed-column-${cellIndex} table-cell`}
                        key={cell.id}
                        style={{
                            padding: `${commonPadding}px`,
                            display: "gird",
                            placeContent: "center",
                            background: colorMode === "light" ? "#fff" : "#282e42",
                            color: colorMode === "light" ? "#0a0a0a" : "",
                        }}
                    >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                );
            })}
        </tr>
    );
};

const MemoizedDraggableRow = React.memo(DraggableRow, (prevProps, nextProps) => {
    return prevProps.row === nextProps.row;
});

const ChecklistTableEnhancer = <T extends object>({
    data,
    columns,
    className = '',
    onPageChange,
    currentPage,
    TotalCount,
    showNavigation = true,
    TotalLength,
    onPerPageChange,
    perPageCount,
    enableColumnFilters = true,
    onColumnFiltersChange,
    loader = false,
    fixedColumn,
    withoutURLFilter,
    onDragEnd,
    dndKey,
}: DataTableProps<T>) => {

    const commonPadding = localStorage.getItem("tPadding") || '13px';
    const table = useReactTable({
        data: data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnFilters: enableColumnFilters,
        pageCount: TotalLength / perPageCount,
        manualPagination: true,
        enableFilters: true,
        enableGlobalFilter: true,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: onColumnFiltersChange,
        getRowId: (row: any) => row._id,
    });

    const [colorMode] = useColorMode();
    const thBackground = colorMode === 'light' ? '#f7efe5' : '#374358';
    const thColor = colorMode === 'light' ? 'black' : '#dadada';

    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    );

    const dataIds = useMemo(() => data?.map((item: any) => item?._id), [data]);

    return (
        <div className="table-responsive">
            <Box as="div" sx={{ paddingLeft: '15px', paddingRight: '15px', paddingBottom: '10px' }}>
                <DndContext
                    key={dndKey}
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                    modifiers={[restrictToVerticalAxis]}
                >
                    <div className={`${fixedColumn ? "tableNotFixHead" : "tableFixHead"}`} style={{ borderRadius: 10, border: `1px solid ${theme?.colors?.muted}`, position: "relative" }}>
                        <table className={`${className + TabelClassname?.PrimaryTabel} mb-0 table-hover`}>
                            <thead className="position-relative">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header: any, index) => {
                                            const isCommentsHeader = header.column.columnDef.header === "Comments";
                                            const isNoColumn = header.column.columnDef.header === "No";

                                            return (
                                                <th className={`th align-middle border-top-0 border-bottom-0 fixed-column-${index} ${isNoColumn ? 'th-no-column' : ''}`}
                                                    key={index}
                                                    style={{ backgroundColor: thBackground, padding: `${commonPadding}px`, fontSize: 14, color: thColor }}>
                                                    <Text variant='Poppins16Normal25lh' sx={{ color: colorMode === "light" ? "black" : "#dadada" }} as={"span"} className="text-center position-relative">
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </Text>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                                    {table.getRowModel().rows.map((row) => (
                                        <MemoizedDraggableRow key={row.id} row={row} fixedColumn={fixedColumn} />
                                    ))}
                                </SortableContext>
                            </tbody>
                        </table>
                        {loader && data?.length === 0 && (
                            <Spinner sx={{ position: "absolute", top: "50%", left: "50%" }} />
                        )}
                        {!loader && data?.length === 0 && (
                            <Text sx={{ position: "absolute", top: "50%", left: "50%", color: thColor }} >No Data Found!</Text>
                        )}
                    </div>
                </DndContext>
            </Box >
            {
                showNavigation ?
                    <Box className="sticky-navigation">
                        <TablePagination
                            onPageChange={(page) => { onPageChange(page) }}
                            onPerPageChange={onPerPageChange}
                            TotalCount={TotalCount}
                            perPageCount={perPageCount}
                            TotalLength={TotalLength}
                            CurrentPage={currentPage}

                        /></Box > : null}
        </div >
    );
};

export default ChecklistTableEnhancer;
